import {
  Table,
  LinearProgress,
  Link,
  Chip,
  Button,
  Box,
  Stack,
  Input,
  Checkbox
} from '@mui/joy';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore } from 'firebase/firestore';
import { format } from 'date-fns';
import Papa from 'papaparse';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Edit } from '@mui/icons-material';

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'submissionDate',
    direction: 'descending'
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();

  const requestSort = key => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const querySnapshot = await getDocs(
          collection(getFirestore(), 'counsellingApplications')
        );
        const appsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          submissionDate: doc.data().submissionDate.toDate()
        }));
        setApplications(appsData);
      } catch (error) {
        console.error('Error fetching applications:', error);
      }
    };

    fetchApplications();
  }, []);

  const sortedAndFilteredApplications = () => {
    const filtered = applications.filter(
      app =>
        app.firstName.toLowerCase().includes(filterText.toLowerCase()) ||
        app.lastName.toLowerCase().includes(filterText.toLowerCase()) ||
        app.email.toLowerCase().includes(filterText.toLowerCase())
    );

    return filtered.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  };

  const preprocessApplication = app => {
    const removeNewlines = str => str.replace(/\n/g, ' ');

    return {
      ...app,
      counsellingFormats: Object.entries(app.counsellingFormats)
        .filter(([key, value]) => value)
        .map(([key]) => key)
        .join(', '),
      address: removeNewlines(
        `${app.address.line1}, ${app.address.city}, ${app.address.province}, ${app.address.postalCode}, ${app.address.country}`
      ),
      dependants: removeNewlines(JSON.stringify(app.dependants)),
      counsellorPreference: removeNewlines(app.counsellorPreference),
      knownHealthIssues: removeNewlines(app.knownHealthIssues)
    };
  };

  const exportToCSV = () => {
    const csvData = selectedRows.map(rowId => {
      const selectedApp = applications.find(app => app.id === rowId); // Look up the application object by ID
      return preprocessApplication(selectedApp);
    });

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'applications.csv');
    link.click();
  };

  const toggleRowSelection = id => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(rowId => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const toggleAllRowsSelection = () => {
    if (selectedRows.length === sortedAndFilteredApplications().length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(sortedAndFilteredApplications().map(app => app.id));
    }
  };

  if (!applications.length) {
    return <LinearProgress />;
  }

  const tableHeaders = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'submissionDate', label: 'Submission Date' },
    { key: 'status', label: 'Status' }
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" my={3}>
        <Input
          placeholder="Filter applications..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
        />
        <Button
          onClick={exportToCSV}
          size="sm"
          variant="soft"
          disabled={selectedRows.length === 0}
        >
          Export to CSV
        </Button>
      </Stack>
      <Table
        hoverRow
        sx={{
          '& thead th:nth-child(1)': { width: '4%' },
          '& thead th:nth-child(4)': { width: '25%' },
          '& thead th:nth-child(6)': { width: '15%' },
          '& tr > *:last-child': {
            width: '111px'
          }
        }}
      >
        <thead>
          <tr>
            <th>
              <Checkbox
                checked={selectedRows.length === applications.length}
                onChange={toggleAllRowsSelection}
                sx={{ verticalAlign: 'sub' }}
              />
            </th>
            {tableHeaders.map(header => (
              <th key={header.key} onClick={() => requestSort(header.key)}>
                <Link underline="none">
                  {header.label}{' '}
                  <SortIcon field={header.key} sortConfig={sortConfig} />
                </Link>
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredApplications().map(application => (
            <tr key={application.id}>
              <td>
                <Checkbox
                  checked={selectedRows.includes(application.id)}
                  onChange={() => toggleRowSelection(application.id)}
                />
              </td>
              <td>{application.firstName}</td>
              <td>{application.lastName}</td>
              <td style={{ wordWrap: 'break-word', maxWidth: '150px' }}>
                {application.email}
              </td>
              <td>{format(application.submissionDate, 'PP')}</td>
              <td>
                <Chip
                  label={application.status}
                  color={
                    application.status === 'approved'
                      ? 'success'
                      : application.status === 'pending'
                      ? 'warning'
                      : 'danger'
                  }
                >
                  {application.status}
                </Chip>
              </td>
              <td>
                <Box display="flex" justifyContent="center">
                  <Button
                    size="sm"
                    variant="soft"
                    color="neutral"
                    startDecorator={<Edit />}
                    onClick={() => navigate(application.id)}
                  >
                    Edit
                  </Button>
                </Box>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Box>
  );
};

const SortIcon = ({ field, sortConfig }) => {
  return sortConfig.key === field ? (
    sortConfig.direction === 'ascending' ? (
      <ArrowDownwardIcon />
    ) : (
      <ArrowUpwardIcon />
    )
  ) : null;
};

export default Applications;
